import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/src/templates/region-page-layout.tsx";
import Scene from '../controls/scene-control';
import Chapter from '../controls/chapter-control';
import YouTube from '../controls/youtube-control';
import Legend from '../controls/legend-control';
import Expando from '../controls/expando-control';
import LinkToScene from '../controls/link-to-scene-control';
import Carousel from '../controls/carousel-control';
import ExternalLink from '../controls/external-link-control';
import Image from '../controls/image-control';
import Icon from '../controls/icon-control';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <Chapter title="" mdxType="Chapter">
  <Scene index={11} mdxType="Scene">
        <h1>{`About the Grampians Region`}</h1>
        <p>{`The Grampians region is in Western Victoria and contains 4% of the state's population. It covers approximately
4.6 million hectares (20% of the state) and extends from the South Australian border in the West to Bacchus Marsh
in the east. The region is comprised of 21.6% public land, which includes areas such as the Wombat State Forest,
Brisbane Ranges, Enfield State Forest, Mt Cole State Forest, Grampians National Park, Little Desert and the Big Desert.`}</p>
        <h1>{`How Victoria manages bushfire risk`}</h1>
        <p>{`In Victoria, we conduct fuel management activities like planned burning, mulching and slashing to
reduce the risk of bushfires causing harm to things we care about. We also conduct planned burning to support plants and animals that need fire for their survival.`}</p>
        <Expando title="more about the strategy" mdxType="Expando">
          <p>{`Fuel management planning involves working with local communities to understand what they value.
We also learn from past bushfires and analyse which areas are most at risk of being impacted by bushfire.`}</p>
          <p>{`We use this information to determine where, when and how we undertake fuel management activities.`}</p>
          <p>{`To ensure our fuel management is informed by local contexts, we've developed unique strategies
for different parts of Victoria, guided by Victoria's Emergency Management regions.`}</p>
          <p>{`Within these regions, other relevant boundaries for bushfire management include Country Fire Authority (CFA)
regional and district boundaries, as well as local government areas.`}</p>
          <p>{`Fuel management is just one of many actions that helps reduce bushfire risk.`}</p>
        </Expando>
        <ExternalLink href="https://www.safertogether.vic.gov.au/" mdxType="ExternalLink">
  Click here for more information on other actions that agencies and communities
  can do to reduce bushfire risk
        </ExternalLink>
        <h1>{`On this page`}</h1>
        <LinkToScene index={20} mdxType="LinkToScene">Identifying what we care about</LinkToScene>
        <LinkToScene index={61} mdxType="LinkToScene">Our bushfire history</LinkToScene>
        <LinkToScene index={91} mdxType="LinkToScene">Bushfire risk in our region</LinkToScene>
        <LinkToScene index={121} mdxType="LinkToScene">Our fuel management strategy</LinkToScene>
        <LinkToScene index={161} mdxType="LinkToScene">Bushfire Risk Engagement Areas</LinkToScene>
  </Scene>
 </Chapter>
    <Chapter title="Identifying what we care about" mdxType="Chapter">
  <Scene index={20} mdxType="Scene">
        <p>{`Ultimately, we manage bushfire risk to protect the things we care about. `}</p>
        <p>{`Our bushfire management planning across the state seeks to protect human life, private property, our economy, critical infrastructure, cultural heritage and the environment.`}</p>
        <p>{`Here in the Grampians region, we have worked with local communities and consulted scientific data and experts to understand where the most important values and assets are in our region and how to protect them from bushfire.`}</p>
        <p>{`Here's some examples of the different values and assets we considered in our
fuel management planning.`}</p>
      </Scene>
      <Scene index={21} mdxType="Scene">
        <h1><Icon icon="child" mdxType="Icon" />{` Human life`}</h1>
        <p>{`Communities within the Grampians region told us that life and property are the most import values to protect
against bushfire.`}</p>
        <p>{`The population of the Grampians region is about 214,600 people. Major towns include Ballarat, Horsham, Bacchus Marsh,
Ararat, Stawell, Daylesford, Hepburn and Beaufort.`}</p>
      </Scene>
      <Scene index={22} mdxType="Scene">
        <p>{`Parts of the region also host large numbers of tourists over weekends and holiday periods, particularly Daylesford,
Hepburn Springs, Halls Gap and the Grampians National Park.`}</p>
      </Scene>
      <Scene index={31} mdxType="Scene">
        <h1><Icon icon="leaf" mdxType="Icon" />{` Natural environment`}</h1>
        <p>{`Protecting our natural environment is important to both residents and visitors in the Grampians region. Public land
provides important habitat for many species, and ensures it is cared for for generations to come. Habitat
also exists on private land.`}</p>
        <Legend colour="rgba(112,168,0,0.7)" mdxType="Legend">Parks and Reserves</Legend>
        <Legend colour="rgba(209,255,115,0.7)" mdxType="Legend">State Forests</Legend>
      </Scene>
      <Scene index={32} mdxType="Scene">
        <p>{`The region is home to several nationally significant threatened species, including the South-eastern Red-tailed
Black-cockatoo, which is found in the West Wimmera and south western parts of the Grampians region.`}</p>
        <Legend colour="rgba(255,0,0,0.4)" mdxType="Legend">Simplified version of South-eastern Red-tailed Black Cockatoo Habitat</Legend>
        <Image src="/images/grampians/grampians_sertbc_rhill.jpg" mdxType="Image">
          <p>{`  The South-eastern Red-tailed Black-cockatoo `}<em parentName="p">{`(Calyptorhynchus banksii
graptogyne)`}</em>{` - Mike Sverns`}</p>
        </Image>
      </Scene>
      <Scene index={33} mdxType="Scene">
        <p>{`The Greater Glider and Wombat Bossiaea are found in the Wombat State Park. We work with recovery groups to ensure
our fire management activities protect these species.`}</p>
        <Legend colour="rgba(132,0,168,0.7)" mdxType="Legend">Greater Glider</Legend>
        <Legend colour="rgba(158,170,215,0.7)" mdxType="Legend">Wombat Bossiaea</Legend>
        <Carousel images={['/images/grampians/greaterglider_adamwhitchurch.jpg', '/images/grampians/wombat-bossiaea-2.jpg']} credits={['Greater Glider - Adam Whitchurch', 'Wombat Bossiaea - Donna Thomas']} mdxType="Carousel" />
      </Scene>
      <Scene index={34} mdxType="Scene">
        <p>{`The Grampians region is also home to common species like the nocturnal Sugar Glider. This species is highly
adaptable and can survive in small patches of bush with varied fire histories, as long as there are tree hollows.`}</p>
        <Legend colour="rgba(215,158,158,0.7)" mdxType="Legend">Sugar Glider</Legend>
 
        <Image src="/images/grampians/sugar_glider-matt_chick.jpg" mdxType="Image">
  Sugar Glider - Matt Chick
        </Image>
        <ExternalLink href="https://www.ffm.vic.gov.au/bushfire-fuel-and-risk-management/plants-and-animals/" mdxType="ExternalLink">
  Click here to learn more about how we consider plants and animals in our
  bushfire management
        </ExternalLink>
      </Scene>
      <Scene index={41} mdxType="Scene">
        <h1><Icon icon="credit-card" mdxType="Icon" />{` Regional economy`}</h1>
        <p>{`Our economy is supported by a range of industries including mineral resources, service industries and manufacturing.`}</p>
        <p>{`Agriculture is a major land use in the region. Over half of Victoria's grain is grown in Yarriambiack Shire.
Horticulture and viticulture are important industries, especially in the Hepburn Shire.`}</p>
        <Legend colour="rgba(255,190,232,0.7)" mdxType="Legend">Agriculture and Horticulture</Legend>
        <Legend colour="rgba(169,0,230,0.7)" mdxType="Legend">Commercial and Industrial</Legend>
        <Legend colour="rgba(156,61,33,0.7)" mdxType="Legend">Extractive Industries</Legend>
        <Legend colour="rgba(0,92,230,0.7)" mdxType="Legend">Forestry (including Plantation)</Legend>
        <Legend colour="rgba(0,115,76,0.7)" mdxType="Legend">Native Vegetation, Parks and Reserves</Legend>
        <Legend colour="rgba(0,255,197,0.7)" mdxType="Legend">Vineyard</Legend>
      </Scene>
      <Scene index={42} mdxType="Scene">
        <p>{`Tourism also significantly contributes to the regional economy, with places like the Daylesford-Macedon corridor and
Halls Gap hosting thousands of tourists each year.`}</p>
      </Scene>
      <Scene index={51} mdxType="Scene">
        <h1><Icon icon="broadcast-tower" mdxType="Icon" />{` Critical infrastructure`}</h1>
        <p>{`The Grampians region is home to a range of critical infrastructure that are important to protect from bushfire.`}</p>
        <p>{`These include Ballarat University and Technology Park, the State Library Storage Facility and several communications
towers throughout the region.`}</p>
        <p>{`Protecting critical infrastructure from bushfire is important for supporting our regional economy and ensuring our
communities have access to essential services.`}</p>
      </Scene>
    </Chapter>
    <Chapter title="Our bushfire history" mdxType="Chapter">
  <Scene index={61} mdxType="Scene">
        <h1>{`Our bushfire history`}</h1>
        <p><em parentName="p">{`This section contains information regarding past bushfires which some readers may find upsetting.
Click to skip ahead to `}</em><LinkToScene index={91} inline mdxType="LinkToScene"><em parentName="p">{`bushfire risk in our region`}</em></LinkToScene><em parentName="p">{` or
`}</em><LinkToScene index={120} inline mdxType="LinkToScene"><em parentName="p">{`our fuel management strategy`}</em></LinkToScene><em parentName="p">{`.`}</em>{` `}</p>
        <p>{`This map shows the bushfire history in the Grampians region from 1938.`}</p>
        <p>{`In the Grampians region, frequent large fires have historically occurred in the Big Desert and
Grampians National Park (traditionally known as Gariwerd), where there are large patches of
continuous vegetation.`}</p>
        <p>{`Fighting fires in these remote areas can be difficult due to the challenging terrain.`}</p>
        <p>{`Historically, there has been a lower number of large bushfires in the eastern half of the
Grampians Region.`}</p>
        <p>{`However, when they have occurred, they've had significant consequences. This is due in part to
the large population located in and around forested areas.`}</p>
        <Legend colour="rgba(115,222,255,0.7)" mdxType="Legend">Fires in 1939</Legend>
        <Legend colour="rgba(95,197,237,0.7)" mdxType="Legend">Fires in the 1940s</Legend>
        <Legend colour="rgba(75,171,219,0.7)" mdxType="Legend">Fires in the 1950s</Legend>
        <Legend colour="rgba(58,147,201,0.7)" mdxType="Legend">Fires in the 1960s</Legend>
        <Legend colour="rgba(42,120,184,0.7)" mdxType="Legend">Fires in the 1970s</Legend>
        <Legend colour="rgba(28,97,166,0.7)" mdxType="Legend">Fires in the 1980s</Legend>
        <Legend colour="rgba(18,76,148,0.7)" mdxType="Legend">Fires in the 1990s</Legend>
        <Legend colour="rgba(8,57,130,0.7)" mdxType="Legend">Fires in the 2000s</Legend>
        <Legend colour="rgba(0,38,115,0.7)" mdxType="Legend">Fires in the 2010s</Legend>
        <Legend colour="rgba(0,38,66,0.7)" mdxType="Legend">Fires in 2020</Legend>
      </Scene>
      <Scene index={71} mdxType="Scene">
        <h1>{`Ash Wednesday`}</h1>
        <p>{`In 1983, on Ash Wednesday, a fire claimed seven people’s lives in the Macedon, East Trentham area.`}</p>
        <p>{`The fire reached a final size of 29,500 hectares, destroying 157 homes and 628 other buildings.`}</p>
        <p>{`Whilst fires of this size have not occurred since, the potential consequence of large fires in
this area remains high.`}</p>
        <Legend colour="rgba(168,0,0,0.7)" mdxType="Legend">Burnt area</Legend>
      </Scene>
      <Scene index={81} mdxType="Scene">
        <h1>{`Mt Lubra`}</h1>
        <p>{`On the 20th of January 2006, lightning started the Mt Lubra fire in the Grampians National Park.`}</p>
        <p>{`The fire resulted in the loss of three lives and hundreds of buildings.`}</p>
        <p>{`Over a two-week period, the Mt Lubra fire had burnt over 127,000 hectares and approximately
47% of the Grampians National Park.`}</p>
        <p>{`Since 2006, the Grampians National Park has experienced another two large-scale fires, both caused by lightning.
These fires have impacted roughly 85% of the Grampians National Park.`}</p>
        <Image src="/images/grampians/mt-lubra-1.jpg" mdxType="Image">
  Mt Lubra Fire 2006 - Glenn Rudolph
        </Image>
        <ExternalLink href="https://www.ffm.vic.gov.au/history-and-incidents/past-bushfires/" mdxType="ExternalLink">
  Click here to learn more about Victoria’s bushfire history
        </ExternalLink>
      </Scene>
    </Chapter>
    <Chapter title="Bushfire risk in our region" mdxType="Chapter">
  <Scene index={91} mdxType="Scene">
        <h1>{`Bushfire risk in our region`}</h1>
        <p>{`This map shows the risk of house loss in the Grampians region. It compares where houses could
be destroyed by bushfire across the region.`}</p>
        <p>{`Different shades represent different levels of risk. As the shades progress from yellow through red to purple,
more and more houses are potentially destroyed.`}</p>
        <p>{`The purple areas have the highest risk of house loss. More houses could potentially be destroyed by bushfire in
these areas than in any other areas in this region.`}</p>
        <Legend colour="rgba(132,0,168,0.7)" mdxType="Legend">Highest Risk</Legend>
        <Legend colour="rgba(168,0,0,0.7)" mdxType="Legend">Higher Risk</Legend>
        <Legend colour="rgba(255,0,0,0.37)" mdxType="Legend">High Risk</Legend>
        <Legend colour="rgba(224,132,101,0.7)" mdxType="Legend">Intermediate Risk</Legend>
        <Legend colour="rgba(255,170,0,0.7)" mdxType="Legend">Low-Intermediate Risk</Legend>
        <p>{`Computer simulations are used to help us estimate houses lost from bushfires. The simulations measure
how damaging bushfires could be in different locations. They assume that the fuels in the landscape
have increased to the highest level.`}</p>
        <p>{`This helps us plan where, how often and how much fuel management we do to reduce risks to communities
over years, or even decades.`}</p>
        <YouTube id="R8QihtP-lNQ" mdxType="YouTube">
  Example of bushfire simulation using Phoenix RapidFire software.
        </YouTube>
        <p>{`This map does not reflect any recent bushfires or activities that could change the risk in the region.
Importantly, this map shows where there is potential for a bushfire to destroy more houses compared to other parts of the region. It does not show risk to individual houses.`}</p>
      </Scene>
      <Scene index={101} mdxType="Scene">
        <h1>{`Higher risk area: Daylesford`}</h1>
        <p>{`Daylesford is considered a high-risk town within the Grampians region. This is due to the large amount of forest
located to north, west and south of the town, as well as its population. This forest could allow fires to become large
and intense before impacting the town. Prediction modelling tells us that many fires can reach Daylesford and cause
house loss.`}</p>
      </Scene>
      <Scene index={111} mdxType="Scene">
        <h1>{`Lower risk area: Lethbridge`}</h1>
        <p>{`In contrast, Lethbridge is located away from forest and surrounded by mostly grass. While grass fuels burn quickly,
house loss from grass fires is less likely than in forest fires. This is because grass fires are not as hot and cause
less embers. Fire history and prediction modelling tells us Lethbridge would experience less house losses.`}</p>
        <ExternalLink href="https://www.safertogether.vic.gov.au/understanding-risk/" mdxType="ExternalLink">
  Click here to learn more about our computer simulations and how we measure risk
        </ExternalLink>
        <ExternalLink href="https://www.cfa.vic.gov.au/plan-prepare/am-i-at-risk" mdxType="ExternalLink">
  Click here to learn more about understanding your risk
        </ExternalLink>
      </Scene>
    </Chapter>
    <Chapter title="Our fuel management strategy" mdxType="Chapter">
  <Scene index={120} mdxType="Scene">
        <h1>{`Our fuel management strategy`}</h1>
        <p>{`By understanding what communities care about, where bushfires have happened before and analysing what areas in the
Grampians region are most at risk, we can create a strategy. This strategy tells agencies where,
how much and how often to conduct fuel management activities to reduce bushfire risk.`}</p>
      </Scene>
  <Scene index={121} mdxType="Scene">
        <p>{`We classify patches of public land as different Fire Management Zones - Asset Protection Zones,`}</p>
        <Legend colour="rgba(226,51,90,0.7)" mdxType="Legend">Asset Protection Zone</Legend>
  </Scene>
  <Scene index={122} mdxType="Scene">
        <p>{`Bushfire Moderation Zones,`}</p>
        <Legend colour="rgba(241,171,102,0.7)" mdxType="Legend">Bushfire Moderation Zone</Legend>
  </Scene>
  <Scene index={123} mdxType="Scene">
        <p>{`Landscape Management Zones`}</p>
        <Legend colour="rgba(244,233,55,0.7)" mdxType="Legend">Landscape Management Zone</Legend>
  </Scene>
  <Scene index={124} mdxType="Scene">
        <p>{`and Planned Burning Exclusion Zones.`}</p>
        <Legend colour="rgba(2,177,86,0.7)" mdxType="Legend">Planned Burning Exclusion Zone</Legend>
        <p>{`In each of these zones we manage fuels differently in terms of the frequency and intensity of fuel management
activities like planned burning.`}</p>
        <p>{`Where we conduct fuel management each year is determined by a range of factors, including fuel loads and weather conditions, such as rainfall.`}</p>
        <p>{`'Fuel' is any grass, leaf litter, twigs, bark and other live vegetation which can burn. Fuel builds up at
different rates in different areas depending on how fast the vegetation grows compared to how fast it decomposes.`}</p>
        <p>{`The way we conduct fuel management in the different types of Fire Management Zones on public land helps us to reduce the intensity,
severity and spread of bushfires across the landscape. This contributes to reducing the risk of bushfires impacting
things we care about.`}</p>
        <p>{`We monitor the success of our fuel management program and publish the results in our yearly Fuel Management Report.`}</p>
  </Scene>
      <Scene index={131} mdxType="Scene">
        <h1>{`Wombat State Forest`}</h1>
        <p>{`To protect human life and private property, we manage fuels around communities close to forested areas, such as
Wombat State Forest and Lerderderg State Park.`}</p>
        <p>{`We've zoned areas around townships like Daylesford, Blackwood and Trentham as `}<strong parentName="p">{`Asset Protection Zones`}</strong>{`.`}</p>
        <p>{`Here, we aim to greatly reduce fine fuels through regular planned burning or mechanical treatments.`}</p>
      </Scene>
      <Scene index={132} mdxType="Scene">
        <p>{`We also conduct fuel management in forest further away from townships in `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{`.`}</p>
        <p>{`We've placed these zones to the west of townships, as this is the direction from which bushfires will
commonly approach. Here, we conduct planned burning every 12-15 years.`}</p>
        <p>{`By managing fuels in both these zones, we aim to protect nearby assets by reducing radiant heat and
threat from embers.`}</p>
      </Scene>
      <Scene index={133} mdxType="Scene">
        <p>{`The remaining forested areas are zoned as `}<strong parentName="p">{`Landscape Management Zones`}</strong>{`. Here, we conduct planned burning to
reduce the potential spread of large fires and to protect environmental values.`}</p>
      </Scene>
      <Scene index={141} mdxType="Scene">
        <h1>{`Little Desert National Park`}</h1>
        <p>{`We conduct fuel management in and around Little Desert National Park for a range of different objectives.`}</p>
        <p>{`We've placed fuel managed breaks within and around the edge of the park to protect the natural environment,
as well as private property and economic assets located near the park.`}</p>
        <p>{`These edge breaks are zoned as `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{`. Here, we conduct regular burns to reduce fine fuels.
This reduces the likelihood of fires both entering and exiting the park.`}</p>
      </Scene>
      <Scene index={142} mdxType="Scene">
        <p>{`Similarly, we have created a network of `}<strong parentName="p">{`Landscape Management Zone`}</strong>{` internal breaks to reduce the potential for large
bushfires that would impact on the ecology of the area.`}</p>
        <p>{`In future, following additional planning, we intend to conduct burns in the remaining `}<strong parentName="p">{`Landscape Management Zones`}</strong>{`
to both reduce large fires and develop a diversity and the age of vegetation in the park.`}</p>
        <p>{`Different ages in vegetation contributes to providing important habitat for native animal species such as Malleefowl
and Silky Mouse.`}</p>
        <Image src="/images/grampians/2020-fire.jpg" mdxType="Image">
  A bushfire running into a Little Desert Break, 2020 - DELWP
        </Image>
      </Scene>
      <Scene index={151} mdxType="Scene">
        <h1>{`Grampians National Park`}</h1>
        <p>{`Over the last twenty years, over 85% of the Grampians National Park has been impacted by bushfires.`}</p>
        <p>{`We've zoned the majority of the park as `}<strong parentName="p">{`Landscape Management Zones`}</strong>{`. Here, we conduct low intensity burning to reduce
the risk of large fires and to protect ecologically sensitive areas.`}</p>
        <p>{`We prioritise where we burn based on fuel loads and other factors that might cause fires to start or spread
quickly, such as topography.`}</p>
      </Scene>
      <Scene index={152} mdxType="Scene">
        <p>{`We also identify areas within these `}<strong parentName="p">{`Landscape Management Zones`}</strong>{` where burning might benefit native plants and
animals.`}</p>
        <p>{`By burning in this way, we can contribute to protecting our natural environment, the tourism industry that
relies on the Grampians National Park, as well as life and property in surrounding towns.`}</p>
        <p>{`We've also zoned some parts of Grampians National Park as `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{`.`}</p>
        <p>{`We conduct fuel management in these areas every 12-15 years. Here, we aim to reduce the intensity
of fires on the western side of the Serra Range.`}</p>
        <p>{`Conducting fuel management in these `}<strong parentName="p">{`Bushfire Moderation Zones`}</strong>{` also helps to reduce the likelihood
of embers impacting Halls Gap, Pomonal and the Mount William Range.`}</p>
      </Scene>
      <Scene index={153} mdxType="Scene">
        <ExternalLink href="https://www.ffm.vic.gov.au/bushfire-fuel-and-risk-management/planned-burning-in-victoria" mdxType="ExternalLink">
  Click here to learn more about our fuel management program on public land
        </ExternalLink>
        <ExternalLink href="https://www.safertogether.vic.gov.au/__data/assets/pdf_file/0027/493533/DELWP_BushfireManagementStrategies_2020_Grampians_rr.pdf" mdxType="ExternalLink">
  Click here to read our full Bushfire Management Strategy for the Grampians
  Region
        </ExternalLink>
      </Scene>
    </Chapter>
    <Chapter title="Bushfire Risk Engagement Areas" mdxType="Chapter">
  <Scene index={161} mdxType="Scene">
        <h1>{`Bushfire Risk Engagement Areas (BREAs)`}</h1>
        <p>{`To protect the things we care about, agencies and communities need to consider ways of reducing bushfire
risk across both public and private land.`}</p>
        <p>{`On public land, our fuel management activities are undertaken across different Fire Management Zones.`}</p>
        <p>{`To support bushfire risk reduction on private land, we've identified `}<strong parentName="p">{`Bushfire Risk Engagement Areas`}</strong>{`.`}</p>
        <Legend colour="rgba(223,115,225,0.7)" mdxType="Legend">Bushfire Risk Engagement Areas</Legend>
        <p>{`Bushfire Risk Engagement Areas in the Grampians cover both public and private land and help fire agencies to
prioritise engagement with the community about managing fuels where it can be most effective. This includes
considering a range of actions to reduce bushfire risk.`}</p>
        <p>{`Managing fuels on private and public land begins with a conversation about the benefits and limitations of fuel
reduction to meet the needs of our local community and environment.`}</p>
        <p>{`Where and how we engage with the community will also be informed by current conditions, such as fuel loads and rainfall.`}</p>
        <p>{`There is no new legal obligation for landowners within Bushfire Risk Engagement Areas to reduce bushfire fuels
on their property.`}</p>
        <p>{`By working together, land and agencies and communities will be able to better protect the things we value from
being impacted by bushfire.`}</p>
        <p>{`For more information or to get involved in fuel management activities on private land, `}<a href="https://www.cfa.vic.gov.au/contact/" target="_blank" rel="noopener noreferrer">{`contact your local CFA District`}</a>{`.`}</p>
      </Scene>
      <Scene index={162} mdxType="Scene">
        <h1>{`Wheatsheaf`}</h1>
        <p>{`The Wheatsheaf Firescape area is in the Wombat Forest around 10km north-east of Daylesford.  The project includes
roughly 1,050 ha private land. This area is considered a high bushfire risk; the Wombat – Wheatsheaf Estate is
listed in the Victorian Fire Risk Register as extreme.`}</p>
        <p>{`The project is being led by CFA in conjunction with DELWP, Hepburn Shire and Dja Dja Wurrung, the Traditional Owners
of the Country.`}</p>
        <p>{`Over 230 residents in the Wheatsheaf, Coomoora and Glenlyon areas were contacted to be part of the firescape project,
of which 113 properties are involved.`}</p>
        <p>{`Fire prevention works vary across the different properties, with some requiring no treatment as vegetation is well
maintained.  On other properties, grooming (mulching), candling and planned burning will be completed to manage the
fire risk posed by vegetation.  Dja Dja Wurrung will carry out several burns using traditional methods.`}</p>
        <p>{`Works on private land are complemented by nearby planned burns and mechanical fuel treatment on public land managed
by DELWP.`}</p>
        <Carousel images={['/images/grampians/wheatsheaf_2.jpg', '/images/grampians/wheatsheaf_3.jpg']} credits={['Landowners and agencies discussing fuel management in Wheatsheaf - Ian Morrison', 'Conducting non-burn fuel treatment works in Wheatsheaf - Ian Morrison']} mdxType="Carousel" />
      </Scene>
      <Scene index={163} mdxType="Scene">
        <h1>{`Moyston Common`}</h1>
        <p>{`The Moyston Common is managed by a Committee of Management (CoM) and has regularly been burnt by CFA to provide
bushfire protection for the township.`}</p>
        <p>{`In recent years the areas adjacent to the planned burn site and on the crownland have become inundated with `}<em parentName="p">{`Acacia
Paradoxa`}</em>{` (Hedge Wattle). This has negatively impacted on the environmental values of the area and significantly
contributed to an increase in the bushfire risk on the west and south side of the township. `}</p>
        <p>{`CFA through the Safer Together Partnership Program and in conjunction FFMvic collaborated with the CoM , local
residents and Landcare to develop and implement a non-burn fuel treatment to control the Hedge Wattle.`}</p>
        <p>{`CFA engaged a private contractor to undertake works in the CoM area and FFMVic provided further support by mulching
the crown land area to the south of the township.`}</p>
        <p>{`The result was a significant reduction in the bushfire risk and will facilitate Landcare’s capacity to manage the
environment through native grass planting and maintenance of the site for the local community and visitors. `}</p>
        <Image src="/images/grampians/moyston_1.jpg" mdxType="Image">
  Moyston Common after being mulched - CFA
        </Image>  
        <ExternalLink href="https://www.safertogether.vic.gov.au/strategic-bushfire-management-planning" mdxType="ExternalLink">
  Click here to learn more about Bushfire Risk Engagement Areas
        </ExternalLink>
  </Scene>
    </Chapter>
    <Chapter title="Thank you" mdxType="Chapter">
      <Scene index={200} mdxType="Scene">
        <p>{`This concludes our Grampians region strategy. You can find out more information
by downloading the full Bushfire Management Strategy. `}</p>
        <p>{`Return to the state view to explore other regional strategies.`}</p>
        <ExternalLink href="https://www.safertogether.vic.gov.au/__data/assets/pdf_file/0027/493533/DELWP_BushfireManagementStrategies_2020_Grampians_rr.pdf" mdxType="ExternalLink">
  Click here to read our full Bushfire Management Strategy for the Grampians Region
        </ExternalLink>
        <ExternalLink href="https://www.safertogether.vic.gov.au/strategic-bushfire-management-planning" mdxType="ExternalLink">
  Click here to learn more about strategic bushfire management planning, and to download the strategy in accessible format
        </ExternalLink>
      </Scene>
    </Chapter>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      